import {
  Box,
  Dialog,
  DialogContent,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  styled,
} from "@mui/material"
import type { ReactNode } from "react"
import { DiscountChip, EvolutionStatChip } from "../../dataDisplay"

type Channel = {
  name: string
  purchases: {
    mainStat: string
    evolution?: number
  }
  discounts?: {
    total?: string | null
    percentage?: string | null
  }
  id: string
}

export interface PurchaseOtherChannelsDialogProps {
  onClickClose: () => void
  Card: ReactNode
  channels: Channel[]
  withoutDiscount?: boolean
  onRowClick?: (channel: Channel) => void
}

export const PurchaseOtherChannelsDialog = ({
  onClickClose,
  Card,
  channels,
  withoutDiscount = false,
  onRowClick,
}: PurchaseOtherChannelsDialogProps) => {
  return (
    <Dialog open={true} onClose={onClickClose} PaperProps={{ elevation: 1 }}>
      <DialogContent sx={{ pt: 6, pl: 5, pr: 10, pb: 10 }}>
        <Stack gap={4} divider={<Divider flexItem orientation="horizontal" />}>
          <Title>détail achats - canal autres</Title>

          <Box display="grid" gridTemplateColumns="195px 1fr" gap={7.5}>
            {Card}

            <TableContainer>
              <Table>
                <TableBody>
                  {channels.map((channel) => (
                    <Row
                      key={channel.name}
                      onClick={onRowClick ? () => onRowClick(channel) : onRowClick}
                      hover={!!onRowClick}
                    >
                      <TableCell sx={{ pl: "0px!important" }}>
                        <ChannelLayout>
                          <Dot />
                          <TypoChannel>{channel.name}</TypoChannel>
                          {!withoutDiscount && <TypoSubTitle>Remise</TypoSubTitle>}
                        </ChannelLayout>
                      </TableCell>

                      <TableCell>
                        <Stack gap={0.8}>
                          <TypoValue>{channel.purchases.mainStat}</TypoValue>
                          {!withoutDiscount && (
                            <DiscountChip
                              label={`${channel?.discounts?.percentage ?? 0} %`}
                              variant="small"
                              fontSize={12}
                            />
                          )}
                        </Stack>
                      </TableCell>

                      <TableCell sx={{ verticalAlign: "top", fontSize: "unset" }}>
                        <EvolutionStatChip
                          evol={channel.purchases.evolution}
                          arrow
                          border={false}
                          formatOptions={{ signDisplay: "never" }}
                        />
                      </TableCell>
                    </Row>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Stack>
      </DialogContent>
    </Dialog>
  )
}

const Title = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(20),
  lineHeight: theme.typography.pxToRem(20),
  fontWeight: 600,
  textTransform: "uppercase",
}))

// TABLE
const Row = styled(TableRow)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.divider}`,
  "&:last-child": { border: 0 },
  backgroundColor: "transparent!important",
  "&>*:not(:first-of-type)": { paddingLeft: "8px!important" },
  "&>*": { paddingRight: "8px!important" },
}))

const ChannelLayout = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateAreas: `
    "dot channel"
    ". subTitle"
  `,
  gridTemplateColumns: "min-content auto",
  columnGap: theme.spacing(1.5),
  rowGap: theme.spacing(0.8),
  alignItems: "center",
}))

// Cell 1
const Dot = styled(Box)(({ theme }) => ({
  gridArea: "dot",
  width: "10px",
  aspectRatio: "1",
  borderRadius: "50%",
  backgroundColor: theme.palette.primary.dark1,
  alignSelf: "center",
}))
const TypoChannel = styled(Typography)(({ theme }) => ({
  gridArea: "channel",
  color: theme.palette.primary.dark1,
  lineHeight: theme.typography.pxToRem(16),
  paddingRight: theme.spacing(3.5),
}))
const TypoSubTitle = styled(Typography)(({ theme }) => ({
  gridArea: "subTitle",
  color: theme.palette.primary.dark3,
  fontSize: theme.typography.pxToRem(12),
  lineHeight: theme.typography.pxToRem(12),
  alignSelf: "flex-start",
}))

// Cell 2
const TypoValue = styled(Typography)(({ theme }) => ({
  gridArea: "value",
  fontWeight: 600,
  color: theme.palette.common.darkBlue,
  lineHeight: theme.typography.pxToRem(16),
  textWrap: "nowrap",
}))
